import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgForm } from '@angular/forms';
import { createId } from '@core/util/file';
import { BusinessClient, ClientType, SigningOfficer } from '@models/clients';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { CreateStatusType } from '@state/utils/models';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { MatterClientsState } from '@state/matter_clients/matterClients.state';
import { TestHooks } from './create-business-client-side-form.hooks';

interface CompanyVM {
	name: string;
	canadianCompany: boolean;
	signingOfficers: SigningOfficer[];
}

const newSigningOfficer: SigningOfficer = {
	name: '',
	title: '',
	phone: '',
	email: '',
	home: '',
	work: '',
};

const newCompany = (name: string): CompanyVM => ({
	name,
	canadianCompany: true,
	signingOfficers: [],
});

@UntilDestroy()
@Component({
	selector: 'app-aps-create-business-client-side-form',
	templateUrl: './create-business-client-side-form.component.html',
	standalone: false,
})
export class CreateBusinessClientSideFormComponent {
	testHooks = TestHooks;

	@Input() set clientName(_name: string) {
		this.company.name = _name;
	}

	@Input() set phone(phone: string) {
		this.phoneNumber = phone;
	}

	@Input() enableSigningOfficers = false;
	@Input() showPrimaryClientOption = false;
	@Input() type: ClientType.CORPORATION | ClientType.BUILDER;
	@Input() primary = false;
	@Input() minify = false;
	@Output() onCreate = new EventEmitter<BusinessClient>();
	@Output() onCancel = new EventEmitter();

	public _name = '';
	public id: string = createId();
	public error: string;
	public submitted = false;
	public createStatusType = CreateStatusType;
	public company = cloneDeep(newCompany(this._name));
	@Input() fax = '';
	@Input() phoneNumber = '';
	@Input() email = '';
	public ICON = ICON;

	private errorString = 'Unable to create new client. Please try again';

	@Select(MatterClientsState.getCreateStatus)
	createStatus$: Observable<CreateStatusType>;

	addSigningOfficer() {
		this.company.signingOfficers.push(cloneDeep(newSigningOfficer));
	}

	removeSigningOfficer = (index: number) => {
		this.company.signingOfficers.splice(index, 1);
	};

	onSubmit = (form: NgForm) => {
		this.error = null;
		this.submitted = true;
		if (form.invalid) {
			this.error = this.errorString;

			return;
		}

		const data = {
			id: this.id,
			type: this.type,
			name: this.company.name,
			canadianCompany: true,
			phone: this.phoneNumber,
			email: this.email,
			fax: this.fax,
			primary: this.primary,
		};

		this.onCreate.emit(data);
	};

	cancel() {
		this.onCancel.emit();
	}
}
